import type { Data } from '@generated/data'
import { Calendar, Clock } from 'lucide-react'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

/**
 * A single article.
 *
 * This page is the reason the blog can rank at all: until now the site had a
 * listing and no post URL, so there was no page for a search engine to return.
 */

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

const Post = ({ post }: { post: Data.Website.BlogPost }) => {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    keywords: post.tags.join(', '),
    author: {
      '@type': 'Person',
      name: post.author.name,
      jobTitle: post.author.role,
      // Ties the byline to a profile a reader can check, the same way the
      // founders are tied to theirs on Our Story.
      ...(post.author.url ? { sameAs: [post.author.url] } : {}),
    },
    publisher: { '@type': 'Organization', name: 'SOTA Navigator' },
  }

  /*
    The SERP for this article's phrase carries a People Also Ask unit, and a
    question-and-answer block is one of the formats that gets a page quoted in
    an AI Overview. FAQPage is also one of the few structured data types Google
    still supports, so it is worth the markup where Course or ClaimReview is
    not.
  */
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }

  return (
    <>
      <Seo
        title={`${post.title} | SOTA Navigator`}
        description={post.excerpt}
        jsonLd={[articleSchema, faqSchema]}
      />
      <PageLayout>
        {/*
          The header sits on the brand dark band rather than on the page
          background. The site nav is transparent with white links until the
          reader scrolls, so a light page top renders the whole navigation
          invisible. bg-brand is the one colour here that does not follow the
          theme, for exactly that reason.
        */}
        <header data-testid="article-header" className="bg-brand pb-16 pt-32 text-brand-foreground">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="mb-4 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-brand-foreground/15 px-3 py-1 text-xs font-medium text-brand-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="mb-6 text-4xl font-bold tracking-tight text-brand-foreground md:text-5xl">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-brand-foreground/80">
              <span className="font-medium text-brand-foreground">
                {post.author.url ? (
                  <a
                    href={post.author.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {post.author.name}
                  </a>
                ) : (
                  post.author.name
                )}
              </span>
              <span>{post.author.role}</span>
              <span className="flex items-center gap-1">
                <Calendar className="size-4" aria-hidden="true" />
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="size-4" aria-hidden="true" />
                {post.readTime}
              </span>
            </div>
          </div>
        </header>

        <article className="bg-background pb-24 pt-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            {/*
              A short summary above the body, for the reader who arrives from a
              search result and wants the answer before deciding to read. It
              sits above the intro deliberately: below the fold it is just
              another paragraph.
            */}
            <aside
              data-testid="article-tldr"
              className="mb-10 rounded-lg border-l-4 border-primary bg-muted p-6"
            >
              <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">
                In short
              </p>
              <p className="text-base leading-7 text-foreground">{post.tldr}</p>
            </aside>

            <div className="space-y-6 text-lg leading-8 text-foreground">
              {post.intro.map((paragraph) => (
                <p key={paragraph.slice(0, 40)}>{paragraph}</p>
              ))}
            </div>

            {post.sections.map((section) => (
              <section key={section.heading} className="mt-12">
                <h2 className="mb-4 text-2xl font-bold text-primary">{section.heading}</h2>
                <div className="space-y-4 text-lg leading-8 text-foreground">
                  {section.paragraphs?.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
                {section.bullets ? (
                  <ul className="mt-4 list-disc space-y-2 pl-6 text-lg leading-8 text-foreground">
                    {section.bullets.map((bullet) => (
                      <li key={bullet.slice(0, 40)}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}

            {post.faqs.length > 0 ? (
              <section className="mt-16 border-t border-border pt-12">
                <h2 className="mb-8 text-2xl font-bold text-primary">Common questions</h2>
                <dl className="space-y-8">
                  {post.faqs.map((faq) => (
                    <div key={faq.question}>
                      <dt className="mb-2 text-lg font-semibold text-foreground">{faq.question}</dt>
                      <dd className="text-lg leading-8 text-foreground">{faq.answer}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            ) : null}
          </div>
        </article>
      </PageLayout>
    </>
  )
}

export default Post
